import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { Button } from '~/components/button';

export const Route = createFileRoute('/_authed/biometrics/breathing')({
  component: BreathingPage,
});

// Resonance breathing: ~5.5 breaths per minute, exhale longer than inhale.
const DEFAULT_BPM = 5.5;
const TOTAL_DURATION_SEC = 300; // 5 minutes

function cycleTimes(bpm: number) {
  const totalCycle = 60 / bpm; // seconds per breath
  const inhale = totalCycle * 0.4;
  const exhale = totalCycle - inhale;
  return { inhale, exhale, total: totalCycle };
}

function BreathingPage() {
  const [bpm, setBpm] = useState(DEFAULT_BPM);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState<'inhale' | 'exhale'>('inhale');
  const [elapsed, setElapsed] = useState(0);
  const [withTone, setWithTone] = useState(false);
  const [withHaptic, setWithHaptic] = useState(true);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const oscRef = useRef<OscillatorNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);

  const { inhale, exhale, total } = cycleTimes(bpm);

  useEffect(() => {
    if (!running) return;
    const start = Date.now();
    let phaseStart = start;
    let currentPhase: 'inhale' | 'exhale' = 'inhale';
    setPhase('inhale');
    if (withHaptic && 'vibrate' in navigator) navigator.vibrate(40);

    const tick = setInterval(() => {
      const now = Date.now();
      const sinceStart = (now - start) / 1000;
      setElapsed(sinceStart);
      if (sinceStart >= TOTAL_DURATION_SEC) {
        setRunning(false);
        clearInterval(tick);
        return;
      }
      const phaseElapsed = (now - phaseStart) / 1000;
      const target = currentPhase === 'inhale' ? inhale : exhale;
      if (phaseElapsed >= target) {
        currentPhase = currentPhase === 'inhale' ? 'exhale' : 'inhale';
        setPhase(currentPhase);
        phaseStart = now;
        if (withHaptic && 'vibrate' in navigator) navigator.vibrate(40);
      }
    }, 80);

    return () => clearInterval(tick);
  }, [running, inhale, exhale, withHaptic]);

  // Optional ambient tone — a soft sine that opens during inhale and closes during exhale.
  useEffect(() => {
    if (!running || !withTone) {
      oscRef.current?.stop();
      oscRef.current = null;
      audioCtxRef.current?.close().catch(() => {});
      audioCtxRef.current = null;
      return;
    }
    const ctx = new AudioContext();
    audioCtxRef.current = ctx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 220;
    gain.gain.value = 0;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    oscRef.current = osc;
    gainRef.current = gain;
    return () => {
      osc.stop();
      ctx.close().catch(() => {});
    };
  }, [running, withTone]);

  useEffect(() => {
    if (!gainRef.current || !audioCtxRef.current) return;
    const t = audioCtxRef.current.currentTime;
    gainRef.current.gain.cancelScheduledValues(t);
    gainRef.current.gain.linearRampToValueAtTime(
      phase === 'inhale' ? 0.05 : 0.02,
      t + (phase === 'inhale' ? inhale : exhale),
    );
  }, [phase, inhale, exhale]);

  const scale = phase === 'inhale' ? 1 : 0.5;
  const remaining = Math.max(0, TOTAL_DURATION_SEC - elapsed);

  return (
    <section className="mx-auto max-w-2xl px-4 py-12 min-h-[70vh] flex flex-col">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">resonance breathing</h1>
        <Link to="/biometrics" className="text-sm text-muted-foreground hover:text-foreground">
          ← biometrics
        </Link>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        five minutes at {bpm.toFixed(1)} breaths per minute. exhale longer than inhale. works offline.
      </p>

      <div className="flex-1 flex flex-col items-center justify-center py-12">
        <div
          aria-hidden
          className="rounded-full bg-foreground/90"
          style={{
            width: 240,
            height: 240,
            transform: `scale(${scale})`,
            transition: `transform ${phase === 'inhale' ? inhale : exhale}s ease-in-out`,
            opacity: running ? 1 : 0.3,
          }}
        />
        <div className="mt-8 text-sm text-muted-foreground tabular-nums">
          {running ? (
            <>
              {phase} · {Math.floor(remaining)}s remaining
            </>
          ) : (
            <>ready</>
          )}
        </div>
      </div>

      <div className="border-t border-border pt-6 space-y-4">
        <div>
          <label className="text-xs text-muted-foreground">
            breaths per minute: {bpm.toFixed(1)} (default 5.5)
          </label>
          <input
            type="range"
            min={4}
            max={8}
            step={0.1}
            value={bpm}
            disabled={running}
            onChange={(e) => setBpm(Number(e.target.value))}
            className="w-full"
          />
          <div className="text-[11px] text-muted-foreground">
            inhale {inhale.toFixed(1)}s · exhale {exhale.toFixed(1)}s · cycle {total.toFixed(1)}s
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={withTone} onChange={(e) => setWithTone(e.target.checked)} />
            ambient tone
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={withHaptic} onChange={(e) => setWithHaptic(e.target.checked)} />
            haptic
          </label>
        </div>
        <div className="flex gap-2">
          <Button
            disabled={running}
            onClick={() => {
              setElapsed(0);
              setPhase('inhale');
              setRunning(true);
            }}
          >
            start
          </Button>
          <Button variant="ghost" disabled={!running} onClick={() => setRunning(false)}>
            stop
          </Button>
        </div>
      </div>
    </section>
  );
}
